import Dexie, { type Table } from 'dexie';

// Local database. Everything except `meta` is ciphertext: records are packed AES-GCM
// (see @circa/shared/crypto), opened in memory after unlock.

export interface RecordRow {
  id: string;
  kind: string;
  /** '' when not in a vault (IndexedDB can't index null) */
  vaultId: string;
  updatedAt: number;
  deleted: 0 | 1;
  /** 1 = changed here, not yet on the server */
  dirty: 0 | 1;
  /** 1 = never synced (local-only chats) */
  localOnly: 0 | 1;
  data: Uint8Array;
}

export interface BodyRow {
  id: string;
  dirty: 0 | 1;
  updatedAt: number;
  data: Uint8Array;
}

/** Encrypted file bytes cached on this device. */
export interface FileRow {
  id: string;
  data: Uint8Array;
  bytes: number;
  lastAccess: number;
  /** 0 = still needs uploading */
  uploaded: 0 | 1;
  /** 'file' = packed with the data/vault key; 'media' = sealed envelope from an AI job */
  format: 'file' | 'media';
}

/** Ids to delete on the server without touching the local copy (chats switched to local-only). */
export interface ForgetRow {
  id: string;
  kind: string;
  at: number;
}

export interface MetaRow {
  key: string;
  value: unknown;
}

class AppDB extends Dexie {
  records!: Table<RecordRow, string>;
  bodies!: Table<BodyRow, string>;
  files!: Table<FileRow, string>;
  forget!: Table<ForgetRow, string>;
  meta!: Table<MetaRow, string>;

  constructor() {
    // Internal name kept from the first build so existing local data still opens.
    super('circa');
    this.version(1).stores({
      records: 'id, kind, dirty, vaultId',
      bodies: 'id, dirty',
      files: 'id, lastAccess, uploaded',
      forget: 'id',
      meta: 'key',
    });
  }
}

export const db = new AppDB();

export async function getMeta<T>(key: string): Promise<T | undefined> {
  return (await db.meta.get(key))?.value as T | undefined;
}

export async function setMeta(key: string, value: unknown): Promise<void> {
  await db.meta.put({ key, value });
}

export async function deleteMeta(key: string): Promise<void> {
  await db.meta.delete(key);
}

/** Ask the browser not to evict our data under storage pressure. */
export async function requestPersistentStorage(): Promise<boolean> {
  try {
    if (navigator.storage?.persisted && (await navigator.storage.persisted())) return true;
    return (await navigator.storage?.persist?.()) ?? false;
  } catch {
    return false;
  }
}
